/**
 * Domain types for the JarvisGPT (MMRAG) Vue frontend.
 *
 * Mirrors the SSE event vocabulary in `shared/streaming/events.md`. The
 * events are produced by the LangGraph workflow (`graph/unified_graph.py`)
 * via the `SSEEmitter` in `shared/streaming/events.py`.
 */

// ----------------------------------------------------------------------------
// Routes (the 6 conversation routes the workflow can take)
// ----------------------------------------------------------------------------

export type RouteName =
  | 'tool_answer'
  // Label used before 2026-10-20; still present in saved conversation history.
  | 'direct'
  | 'context_retrieval'
  | 'profile_group'
  | 'profile_campaign'
  | 'sector'
  | 'region'
  // Travel risk path: the assessed brief, and the itinerary intake question.
  | 'travel_risk'
  | 'travel_clarify'

export const ROUTE_LABELS: Record<RouteName, string> = {
  tool_answer: 'Direct answer',
  direct: 'Direct answer',
  context_retrieval: 'Context retrieval',
  profile_group: 'Group profile',
  profile_campaign: 'Campaign profile',
  sector: 'Sector analysis',
  region: 'Region analysis',
  travel_risk: 'Travel risk assessment',
  travel_clarify: 'Travel itinerary',
}

// ----------------------------------------------------------------------------
// Chat items rendered in the message list
// ----------------------------------------------------------------------------

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  /** Route classification once the router has decided one (assistant only). */
  route?: RouteName | null
  /** Renders this bubble as a customer-facing error notice (assistant only). */
  error?: boolean
  /** Whether retrying the same request might succeed (drives the Retry CTA). */
  retryable?: boolean
  /** LangSmith run id for this assistant turn; used to key thumbs feedback. */
  runId?: string
  /** The thumbs feedback the user has submitted for this assistant turn. */
  feedback?: 'up' | 'down'
  /** The assistant asked the user a question (e.g. itinerary intake) rather than answering. */
  awaitingReply?: boolean
  /**
   * Raw citations from the backend, keyed (on the client) by URL to look up the
   * risk tier / publisher for inline citation markers. Numbering for markers is
   * derived from order of first appearance in `content`, not from these ids.
   */
  citations?: RawCitation[]
}

// ----------------------------------------------------------------------------
// Risk-tiered inline citations
// ----------------------------------------------------------------------------

/**
 * One event/action the source was cited against, with that event's risk tier.
 * Surfaced in the hovercard so per-event risk isn't lost when a single source
 * is cited against multiple events of differing severity.
 */
export interface CitationAction {
  title?: string
  /** Client-portal impact scale: 1=low..4=very high, or null = N/A. */
  riskTier: number | null
}

/** Raw citation object as emitted by the backend (`evidence_processor`). */
export interface RawCitation {
  id?: number
  title?: string
  url: string
  source_type?: string | null
  quality_score?: number | null
  /** Client-portal impact scale: 1=low..4=very high, or null = N/A. */
  risk_tier?: number | null
  /** Raw impact string from the index, e.g. "2 (medium)". */
  impact?: string | null
  publisher?: string | null
  actions?: { title?: string; risk_tier?: number | null }[]
}

/**
 * A citation as rendered in the UI: stable `id` assigned by order of first
 * appearance in the answer text, deduplicated by URL, enriched with the risk
 * tier of the highest-severity event it was cited against.
 */
export interface Citation {
  id: number
  title: string
  url: string
  /** Client-portal impact scale: 1=low..4=very high, or null = N/A. */
  riskTier: number | null
  publisher?: string
  /** Provenance hint, e.g. `mcp` for aggregation probes (no click-through). */
  sourceType?: string | null
  /** Per-event tier pairings when the source backs events of differing risk. */
  actions?: CitationAction[]
}

export interface ToolActivity {
  id: string
  type: 'tool'
  tool: string
  status: 'executing' | 'complete' | 'error'
  label: string
  detail: string
  /** Number of result documents (for retrieval tools). */
  resultCount?: number | null
  timestamp: number
}

/**
 * Per-graph-node activity bubble. Surfaces what the workflow is doing in
 * real time (e.g. tag extraction, entity resolution, evidence scoring).
 */
export interface NodeActivity {
  id: string
  type: 'node'
  node: string
  status: 'running' | 'complete' | 'error'
  detail: string
  meta?: Record<string, unknown>
  timestamp: number
}

export type ChatItem = ChatMessage | ToolActivity | NodeActivity

export function isTool(item: ChatItem): item is ToolActivity {
  return 'type' in item && item.type === 'tool'
}

export function isNode(item: ChatItem): item is NodeActivity {
  return 'type' in item && item.type === 'node'
}

// ----------------------------------------------------------------------------
// Source citations (right-rail SourcesPanel)
// ----------------------------------------------------------------------------

export interface SourceCitation {
  id: string
  title: string
  url?: string | null
  snippet?: string | null
  /** Provenance: 'azure_search' | 'web_search' | 'perplexity' | 'mcp' | ... */
  provider: string
  score?: number | null
  metadata?: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// Threads (left-rail ThreadList)
// ----------------------------------------------------------------------------

export interface ThreadSummary {
  thread_id: string
  created_at: string
  updated_at: string
  name?: string | null
  message_count: number
  metadata?: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// REST history payloads (from GET /v1/history/{thread_id})
// ----------------------------------------------------------------------------

export interface HistoryThreadMessage {
  role: 'user' | 'assistant' | 'tool' | 'system'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export interface HistoryResponse {
  thread_id: string
  messages: HistoryThreadMessage[]
  metadata?: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// /v1/chat request/response shapes
// ----------------------------------------------------------------------------

export interface ChatRequest {
  message: string
  thread_id?: string
  settings?: Record<string, unknown>
  stream?: boolean
}

export interface ChatResponse {
  thread_id: string
  message: string
  sources: SourceCitation[]
  metadata: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// Identity (from /auth/session/me)
// ----------------------------------------------------------------------------

export interface SessionIdentity {
  authenticated: true
  name: string
  email: string
  subject_id: string
  client_id: string
}
